import lodash from 'lodash'

import { GridProduct, InterfaceProduct, MechProduct, ProductOrientation, SkinProduct } from '../types/productTypes'

import { InflatedListItemProduct } from '../list/ListTypes'
import { LedModule } from '../data/LedModule'
import { isLedSupported } from '../products/productUtils'
import { PriceMap, ProductPriceAvailable, ProductPriceUnavailable } from '../productPricing/ProductPricingContext'
import { PriceTypeLabel } from '../list/PriceTypeLabel'

export interface InterfaceBomItem {
  interfaceItemNo: string
  mechItemNo: string | null
  interfaceIncludedWithSkin: boolean
  interfaceIncludedWithMech: boolean
  addLedEnabled: boolean
  ledIncluded?: boolean
}

export interface InflatedInterfaceBomItem extends InterfaceBomItem {
  interfaceProduct: InterfaceProduct
  parentInterfaceProduct: InterfaceProduct | null
  mechProduct: MechProduct | null
  mechIncludedWithGrid: boolean
}

export interface ProductBom {
  skinItemNo: string
  gridItemNo: string | null
  skinIncluded: boolean
  interfaces: readonly InterfaceBomItem[]
  quantity: number
}

export interface InflatedProductBom extends ProductBom {
  orientation: ProductOrientation
  skinProduct: SkinProduct
  gridProduct: GridProduct | null
  interfaces: readonly InflatedInterfaceBomItem[]
}

export interface InflatedProductBomWithRoom extends InflatedProductBom {
  roomId: string | null
}

export function getBomProductsForInflatedItem(
  product: InflatedListItemProduct,
): InflatedProductBom {
  const quantity = product.quantity
  const skin = product.skinProduct
  const grid = skin.gridProduct
  const skinIncluded = grid?.includedSkinItemNo === skin.itemNo
  const includedMechItemNo = grid?.includedMechItemNo ?? null

  const interfaceProducts = product.configuredButtons.map<InflatedInterfaceBomItem>((item) => {
    const { interfaceProduct, mechProduct } = item
    const parentInterfaceProduct = item.parentInterfaceProduct ?? null

    const interfaceItemNo = interfaceProduct.itemNo

    const baseInterfaceItemNo = parentInterfaceProduct?.itemNo ?? interfaceProduct.itemNo

    const ledIncluded = mechProduct?.includesLed ?? false

    const addLed = product.ledEnabled && isLedSupported(interfaceProduct, mechProduct ?? null)

    // Mech includes either the component or parent item
    const interfaceIncludedWithMech = mechProduct?.includedInterfaceItemNo === baseInterfaceItemNo || mechProduct?.includedInterfaceItemNo === interfaceProduct.itemNo

    return {
      interfaceProduct,
      parentInterfaceProduct,
      mechProduct,
      interfaceItemNo: interfaceItemNo,
      mechItemNo: mechProduct?.itemNo ?? null,
      interfaceIncludedWithMech,
      interfaceIncludedWithSkin: skin.includedRockerItemNo === baseInterfaceItemNo,
      addLedEnabled: addLed,
      mechIncludedWithGrid: includedMechItemNo !== null && mechProduct?.itemNo === includedMechItemNo,
      ledIncluded
    }
  })

  return {
    orientation: skin.orientation,
    skinProduct: skin,
    skinItemNo: skin.itemNo,
    gridProduct: grid,
    gridItemNo: grid?.itemNo ?? null,
    skinIncluded,
    interfaces: interfaceProducts,
    quantity,
  }
}

/**
 * Function to possibly return an overridden pack quantity, should fall back to catalogue value if null
 */
export type PackQuantityProvider = (itemNo: string) => (number | null)

export interface BomItem {
  itemNo: string
  description: string
  individualQuantity: number
  quantity: number
  packSize: number
  image: string
  components: readonly string[]
  usedComponents: readonly string[]
  price?: ProductPriceAvailable | typeof ProductPriceUnavailable
}

export function bomItemFromSkin(skin: SkinProduct, packQuantityProvider: PackQuantityProvider): BomItem {
  return {
    itemNo: skin.itemNo,
    description: skin.description,
    individualQuantity: 0,
    quantity: 0,
    packSize: packQuantityProvider(skin.itemNo) ?? 1,
    image: skin.image,
    components: [],
    usedComponents: [],
  }
}

function bomItemFromGrid(grid: GridProduct, packQuantityProvider: PackQuantityProvider): BomItem {
  return {
    itemNo: grid.itemNo,
    description: grid.description,
    individualQuantity: 0,
    quantity: 0,
    packSize: packQuantityProvider(grid.itemNo) ?? 1,
    image: '',
    components: [],
    usedComponents: [],
  }
}

function bomItemFromInterface(inter: InterfaceProduct, packQuantityProvider: PackQuantityProvider): BomItem {
  return {
    itemNo: inter.itemNo,
    description: inter.description,
    individualQuantity: 0,
    quantity: 0,
    packSize: packQuantityProvider(inter.itemNo) ?? Math.max(1, inter.packQuantity),
    image: inter.image,
    components: inter.includedComponents ?? [],
    usedComponents: [],
  }
}

function bomItemFromMech(mech: MechProduct, packQuantityProvider: PackQuantityProvider): BomItem {
  return {
    itemNo: mech.itemNo,
    description: mech.description,
    individualQuantity: 0,
    quantity: 0,
    packSize: packQuantityProvider(mech.itemNo) ?? 1,
    image: mech.image,
    components: [],
    usedComponents: [],
  }
}

function bomItemFromLed(packQuantityProvider: PackQuantityProvider): BomItem {
  return {
    itemNo: LedModule.partNumber,
    description: LedModule.name,
    individualQuantity: 0,
    quantity: 0,
    packSize: packQuantityProvider(LedModule.partNumber) ?? 10,
    image: '',
    components: [],
    usedComponents: [],
  }
}

/**
 * Generates a Bill of Materials (BoM) from the configured product selection
 *
 * Accounts for the following rules:
 * - composite products include both a skin and grid
 * - skins (and composite products) may include interfaces (button rockers)
 * - grids (and composite products) may include mechs
 * - mechs may include interfaces
 * - interfaces may come included in a pack of different components (which may in turn be included with a mech)
 * - LED modules can be included with come mechs
 *
 * Based on this, some products may be removed from, or additional products mey be added to, the final list
 *
 * @param products list of configured products
 * @param packQuantityProvider getter for item pack quantities
 */
export function generateFullBom(products: readonly Readonly<InflatedProductBom>[], packQuantityProvider: PackQuantityProvider): BomItem[] {
  const bomMap: { [itemNo: string]: BomItem } = {}

  const findOrCreate = (itemNo: string, onCreate: () => BomItem): BomItem => {
    const item = bomMap[itemNo] ?? onCreate()

    bomMap[itemNo] = item
    return item
  }

  interface ParentItemMapping {
    item: InterfaceProduct
    includedChildren: string[]
  }

  // map child component item numbers to parents
  const parentComponentMap: { [itemNo: string]: ParentItemMapping } = { }

  const findOrCreateParentItem = (parentItem: InterfaceProduct): ParentItemMapping => {
    const { itemNo } = parentItem
    const item: ParentItemMapping = parentComponentMap[itemNo] ?? { item: parentItem, includedChildren: [] }

    parentComponentMap[itemNo] = item
    return item
  }

  products.forEach(({ skinProduct, gridProduct, interfaces, skinIncluded, quantity }) => {
    if (!skinIncluded) {
      findOrCreate(skinProduct.itemNo, () => bomItemFromSkin(skinProduct, packQuantityProvider)).individualQuantity += quantity
    }

    if (gridProduct) {
      findOrCreate(gridProduct.itemNo, () => bomItemFromGrid(gridProduct, packQuantityProvider)).individualQuantity += quantity
    }

    interfaces.forEach(({
      interfaceIncludedWithSkin,
      interfaceIncludedWithMech,
      addLedEnabled: led,
      interfaceProduct,
      mechProduct,
      mechIncludedWithGrid,
      parentInterfaceProduct,
      ledIncluded
    }) => {
      // add LED modules to BOM
      if (led && !ledIncluded) {
        findOrCreate(LedModule.partNumber, () => bomItemFromLed(packQuantityProvider)).individualQuantity += quantity
      }

      // Add the interface or parent interface if it is not included
      if (interfaceIncludedWithMech || interfaceIncludedWithSkin) {
        // Included; Nothing to be added
      } else if (parentInterfaceProduct) {
        // Add component to parent item
        lodash.range(0, quantity).forEach(() => {
          const parentMapItem = findOrCreateParentItem(parentInterfaceProduct)
          parentMapItem.includedChildren.push(interfaceProduct.itemNo)
        })
      } else {
        // default: add interface to BOM
        findOrCreate(interfaceProduct.itemNo, () => bomItemFromInterface(interfaceProduct, packQuantityProvider)).individualQuantity += quantity
      }

      // Add mech if it is not included
      if (mechProduct && !mechIncludedWithGrid) {
        findOrCreate(mechProduct.itemNo, () => bomItemFromMech(mechProduct, packQuantityProvider)).individualQuantity += quantity
      }
    })
  })

  // from accumulated child components, add required parent components to BoM
  // the quantity of each parent component is determined by the highest quantity of any child components it has
  // e.g. if:
  //   - ProductA includes Component1 and Component2
  //   - List includes 1 x Component1 and 2 x Component2
  // then: 2 x ProductA are required

  Object.keys(parentComponentMap).forEach(parentItemNo => {
    const parentMapItem = parentComponentMap[parentItemNo]
    const childItemNos = parentMapItem.includedChildren

    const childItemCount = lodash.groupBy(childItemNos)
    const max = Object.keys(childItemCount).reduce((accum, curr) => Math.max(accum, childItemCount[curr].length), 0)

    const parentItem = findOrCreate(parentItemNo, () => bomItemFromInterface(parentMapItem.item, packQuantityProvider))
    parentItem.individualQuantity += max
    parentItem.usedComponents = childItemNos
  })

  return Object.keys(bomMap).map<BomItem>(itemNo => {
    const item = bomMap[itemNo]

    return {
      ...item,
      quantity: Math.ceil(item.individualQuantity / item.packSize),
    }
  })
}

export function addPricingToBomItems(bom: readonly BomItem[], pricing: PricingParams | null): readonly BomItem[] {
  if (pricing == null) {
    return bom
  }

  return bom.map(bomItem => ({
    ...bomItem,
    price: pricing.priceMap[bomItem.itemNo]?.times(bomItem.quantity) ?? ProductPriceUnavailable,
  }))
}

export function generateExportDataFromBomData(bom: readonly BomItem[], pricing: PricingParams | null): string[][] {
  const bomItems = addPricingToBomItems(bom, pricing)

  return [
    [ 'Part Number', 'Description', 'Order Qty', 'Qty Required', 'Pack Qty', ...(pricing ? [pricing.label.label] : []) ],
    [ '', '', 'Your order', 'Your requirement', '' ],
    ...bomItems.map<string[]>(it =>
      [ it.itemNo, it.description, `${it.quantity}`, `${it.individualQuantity}`, `${it.packSize}`, ...(pricing ? [it.price?.toFixed(2) ?? 'Unavailable'] : []) ]
    ),
  ]
}

export interface PricingParams {
  label: PriceTypeLabel
  priceMap: PriceMap
}

export function generateExportDataFromListItems(products: readonly InflatedListItemProduct[], packQuantityProvider: PackQuantityProvider, pricing?: PricingParams | null): string[][] {
  const bom = generateFullBom(products.map(it => getBomProductsForInflatedItem(it)), packQuantityProvider)

  return generateExportDataFromBomData(bom, pricing ?? null)
}
